import { useState } from "react";
import { Pressable } from "react-native";
import type { Href } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { ExternalLink } from "@src/components/ExternalLink";
import { Icon } from "@src/components/Icon";
import {
  activationCameraKey,
  chronological,
  formatElapsed,
  type ActivationDetail,
  type ActivationOperatorNote,
  type ActivationSignal,
  type ActivationSignalProcessing,
} from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant, formatText } from "../format";
import { Empty, Field, InlineLink, Section } from "./DetailSection";

interface ActivationDetailViewProps {
  readonly activation: ActivationDetail;
  readonly tenant: { readonly label: string; readonly href?: Href };
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  /** Omit where the camera page cannot be reached; the camera then shows unlinked. */
  readonly cameraHref?: (siteId: string, cameraKey: string) => Href;
  /** Omit where there is no tenant list to narrow; site and camera then show unlinked. */
  readonly listHref?: (filters: { site: string; camera?: string }) => Href;
}

export function ActivationDetailView({
  activation,
  tenant,
  siteLabel,
  cameraLabel,
  cameraHref,
  listHref,
}: ActivationDetailViewProps) {
  const cameraKey = activationCameraKey(activation);
  const site = activation.a3sSite;

  return (
    <View gap="4xl">
      <Section title="Summary">
        <Field label="Ticket">{activation.ticketNo}</Field>
        <Field label="Description">{formatText(activation.description)}</Field>
        <Field label="Pending state">{formatText(activation.pendingState)}</Field>
        <Field label="Completed status">{formatText(activation.completedStatus)}</Field>
        <Field label="Reason">{formatText(activation.reason)}</Field>
        <Field label="Priority">{activation.priority !== undefined ? String(activation.priority) : EMPTY}</Field>
        <Field label="Organisation">
          {tenant.href !== undefined ?
            <InlineLink href={tenant.href}>{tenant.label}</InlineLink>
          : tenant.label}
        </Field>
        <Field label="Site">
          {site === undefined ?
            EMPTY
          : listHref !== undefined ?
            <InlineLink href={listHref({ site })}>{siteLabel(site)}</InlineLink>
          : siteLabel(site)}
        </Field>
        <Field label="Camera">
          {site !== undefined && cameraKey !== undefined ?
            <View flexDirection="row" gap="lg" flexWrap="wrap">
              {cameraHref !== undefined ?
                <InlineLink href={cameraHref(site, cameraKey)}>{cameraLabel(cameraKey)}</InlineLink>
              : <Text textStyle="sm/regular" color="text" selectable>
                  {cameraLabel(cameraKey)}
                </Text>
              }
              {listHref !== undefined && (
                <InlineLink href={listHref({ site, camera: cameraKey })}>Other activations for this camera</InlineLink>
              )}
            </View>
          : EMPTY}
        </Field>
        {cameraKey !== undefined && <Field label="Camera key">{cameraKey}</Field>}
      </Section>

      <Section title="Timeline">
        <Field label="Created">{formatInstant(activation.createdDateTime)}</Field>
        <Field label="Responded">
          <TimelineValue
            from={activation.createdDateTime}
            at={activation.respondedDateTime}
            sinceLabel="after created"
          />
        </Field>
        <Field label="Completed">
          <TimelineValue
            from={activation.respondedDateTime ?? activation.createdDateTime}
            at={activation.completedDateTime}
            sinceLabel={activation.respondedDateTime !== undefined ? "after responded" : "after created"}
          />
        </Field>
      </Section>

      <Section title={`Signals (${String(activation.signals.length)})`}>
        {activation.signals.length === 0 ?
          <Empty>No signals on this activation.</Empty>
        : chronological(activation.signals, (s) => s.receivedDateTime).map((signal, index) => (
            <SignalRow key={`${signal.receivedDateTime}#${String(index)}`} signal={signal} isFirst={index === 0} />
          ))
        }
      </Section>

      <Section title={`Operator notes (${String(activation.operatorNotes.length)})`}>
        {activation.operatorNotes.length === 0 ?
          <Empty>No operator notes on this activation.</Empty>
        : chronological(activation.operatorNotes, (n) => n.noteDateTime).map((note, index) => (
            <OperatorNoteRow key={note.noteId} note={note} isFirst={index === 0} />
          ))
        }
      </Section>
    </View>
  );
}

function TimelineValue({ from, at, sinceLabel }: { from: string; at: string | undefined; sinceLabel: string }) {
  if (at === undefined) {
    return (
      <Text textStyle="sm/regular" color="text">
        {EMPTY}
      </Text>
    );
  }
  const elapsed = formatElapsed(from, at);
  return (
    <Text textStyle="sm/regular" color="text" selectable>
      {formatInstant(at)}
      {elapsed !== undefined && (
        <Text textStyle="xs/regular" color="textMuted">
          {`  ${elapsed} ${sinceLabel}`}
        </Text>
      )}
    </Text>
  );
}

function SignalRow({ signal, isFirst }: { signal: ActivationSignal; isFirst: boolean }) {
  return (
    <View style={[styles.entry, !isFirst && styles.entryDivider]}>
      <Field label="Received">{formatInstant(signal.receivedDateTime)}</Field>
      <Field label="Description">{formatText(signal.description)}</Field>
      <Field label="Notes">{formatText(signal.notes)}</Field>
      <Field label="a3s signal ID">{formatText(signal.a3sSignalId)}</Field>
      {/* The key, not the URL: the signed query string is noise, and the key is what AWS tooling takes. */}
      <Field label="Clip">
        {signal.a3sClip !== undefined ?
          <ExternalLink href={signal.a3sClip.url} style={styles.inlineLink}>
            <Text textStyle="sm/regular" style={styles.link}>
              {signal.a3sClip.key}
            </Text>
          </ExternalLink>
        : EMPTY}
      </Field>
      {signal.a3sProcessing !== undefined ?
        <SignalProcessing processing={signal.a3sProcessing} />
      : <Field label="a3s processing">{EMPTY}</Field>}
    </View>
  );
}

/** a3s's handling of the signal, collapsed: it is for investigating, not for reading every signal. */
function SignalProcessing({ processing }: { processing: ActivationSignalProcessing }) {
  const [expanded, setExpanded] = useState(false);
  const { theme } = useUnistyles();
  const { triage, delivery, failures } = processing;
  const settledLabel = SETTLED_LABEL[processing.status];

  return (
    <View style={styles.processing}>
      <Pressable
        onPress={() => setExpanded((prev) => !prev)}
        style={styles.processingToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
      >
        <Icon name={expanded ? "chevronUp" : "chevronDown"} size={16} color={theme.colors.textMuted} />
        <Text textStyle="sm/medium" color="textMuted">
          a3s processing
        </Text>
      </Pressable>
      {expanded && (
        <View gap="md">
          <Field label="Triage">
            {triage.status === "classified" ?
              [triage.response, triage.outcome, triage.model && `(${triage.model})`].filter(Boolean).join(" · ")
            : TRIAGE_ABSENT[triage.status]}
          </Field>
          {triage.status === "classified" && (
            <>
              <Field label="Threat score">{formatScore(triage.threatScore)}</Field>
              <Field label="Detection confidence">{formatScore(triage.detectionConfidence)}</Field>
              <Field label="Summary">{formatText(triage.summary)}</Field>
            </>
          )}
          <Field label="Status">{STATUS_LABEL[processing.status]}</Field>
          <Field label="Provider">{processing.provider}</Field>
          {delivery !== undefined && (
            <>
              <Field label="Action">
                {delivery.existingTicketNo !== undefined ?
                  `${delivery.action} · open activation ${delivery.existingTicketNo}`
                : delivery.action}
              </Field>
              {delivery.mapping !== undefined && (
                <Field label="Mapping">
                  {delivery.mapping.classificationKey === delivery.mapping.deliveredKey ?
                    `${delivery.mapping.deliveredKey} (no remap)`
                  : `${delivery.mapping.classificationKey} → ${delivery.mapping.deliveredKey} (remapped)`}
                </Field>
              )}
              <Field label="Delivered as">
                {[
                  delivery.typeNo !== undefined && `Type ${delivery.typeNo}`,
                  delivery.mapping !== undefined && `Priority ${delivery.mapping.deliveredPriority}`,
                ]
                  .filter(Boolean)
                  .join(" · ") || EMPTY}
              </Field>
              <Field label="Policy">{delivery.policy.length > 0 ? delivery.policy.join("\n") : EMPTY}</Field>
            </>
          )}
          <Field label="Captured">{formatInstant(processing.capturedAt)}</Field>
          <Field label="Received by a3s">
            <TimelineValue from={processing.capturedAt} at={processing.receivedAt} sinceLabel="after captured" />
          </Field>
          {settledLabel !== undefined && (
            <Field label={settledLabel}>
              <TimelineValue from={processing.receivedAt} at={processing.settledAt} sinceLabel="after received" />
            </Field>
          )}
          <Field label={`Failed attempts (${String(failures.length)})`}>
            {failures.length === 0 ?
              EMPTY
            : failures
                .map((f) => `${formatInstant(f.at)} · ${f.transient ? "transient" : "permanent"} · ${f.reason}`)
                .join("\n")
            }
          </Field>
        </View>
      )}
    </View>
  );
}

const TRIAGE_ABSENT = {
  test_signal: "Skipped: test signal",
  none: "None recorded: triage failed or timed out, or this provider does not triage",
} as const;

const STATUS_LABEL = {
  pending: "Pending delivery",
  delivered: "Delivered",
  poisoned: "Poisoned: delivery gave up",
  expired: "Expired before delivery",
} as const;

const SETTLED_LABEL = {
  pending: undefined,
  delivered: "Delivered",
  poisoned: "Poisoned",
  expired: "Expired",
} as const;

/** A 0–1 score to two places — the scale the classifier reports on. */
function formatScore(score: number | undefined): string {
  return score === undefined ? EMPTY : score.toFixed(2);
}

function OperatorNoteRow({ note, isFirst }: { note: ActivationOperatorNote; isFirst: boolean }) {
  return (
    <View style={[styles.entry, !isFirst && styles.entryDivider]}>
      <Field label="Recorded">{formatInstant(note.noteDateTime)}</Field>
      <Field label="Type">{formatText(note.noteType)}</Field>
      <Field label="Description">{formatText(note.description)}</Field>
      {/* Free text written by a monitoring operator; line breaks are theirs. */}
      <Field label="Notes">{formatText(note.notes)}</Field>
      <Field label="Operator">{formatText(note.operatorLogin)}</Field>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  inlineLink: { alignSelf: "flex-start" },
  link: { color: theme.colors.interactive.buttons.secondary_foreground, textDecorationLine: "underline" },
  entry: { gap: theme.spacing.xs, paddingTop: theme.spacing.md },
  entryDivider: { borderTopWidth: 1, borderColor: theme.colors.borderCard },
  processing: { gap: theme.spacing.md, paddingTop: theme.spacing.xs },
  processingToggle: { flexDirection: "row", alignItems: "center", gap: theme.spacing.xs, alignSelf: "flex-start" },
}));
